import { JOBS_ENDPOINT, JOB_STATUS_COLOR_MAP, JOB_KIND_COLOR_MAP, getJobKindChipSx } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import {
  Alert,
  Box,
  Typography,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { AddressAccordeon } from "../../../../components/ui/clientAddressAccordion/clientAddressAccordion.component";
import { EquipmentAttributeValuesAccordion } from "../../../../components/ui/equipmentAttributeValuesAccordion/equipmentAttributeValuesAccordion.component";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission, useHasAnyPermission } from "../../../../contexts/auth";
import { formatCounterValue, formatDate, formatDateTime } from "../../../../utils";
import { JobTask, JobUsedPart } from "../../../../types/jobs";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

type JobBasicInfoProps = {
  previewId?: string | number;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

// Canonical display order for counter kinds - Black pages, then Color pages,
// then Scanned pages - regardless of the order they come back from the API.
const COUNTER_KIND_ORDER: Record<string, number> = { BP: 0, CP: 1, SP: 2 };

// All counter(s) actually assigned to this specific JobTask (JobTask.counters -
// a task can have several, e.g. one per kind), shown as a small table - not
// the equipment's globally-latest readings.
const TaskCounterTable = ({ counters }: { counters: JobTask["counters"] }) => {
  const { t } = useTranslation();

  if (!counters || counters.length === 0) {
    return null;
  }

  const sortedCounters = [...counters].sort(
    (a, b) => (COUNTER_KIND_ORDER[a.kind] ?? 99) - (COUNTER_KIND_ORDER[b.kind] ?? 99)
  );

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        <strong>{t("Counter")}:</strong>
      </Typography>
      <Box
        sx={{
          ml: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Kind")}</TableCell>
                <TableCell>{t("Value")}</TableCell>
                <TableCell>{t("Read at")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCounters.map((counter) => (
                <TableRow key={counter.id}>
                  <TableCell>{counter.kind_display || "-"}</TableCell>
                  <TableCell>{formatCounterValue(counter.value)}</TableCell>
                  <TableCell>{counter.read_at ? formatDateTime(counter.read_at) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

// Parts used on this specific JobTask - just what was used and how much,
// nothing else (no pricing/order details; that lives on the Part/Stock pages).
const TaskUsedPartsTable = ({ usedParts }: { usedParts: JobUsedPart[] }) => {
  const { t } = useTranslation();

  if (!usedParts || usedParts.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        <strong>{t("Used parts")}:</strong>
      </Typography>
      <Box
        sx={{
          ml: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Part")}</TableCell>
                <TableCell>{t("Quantity")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usedParts.map((usedPart) => (
                <TableRow key={usedPart.id}>
                  <TableCell>{usedPart.ordered_part?.display_name || "-"}</TableCell>
                  <TableCell>{usedPart.quantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export const JobBasicInfo = ({ previewId }: JobBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(JOBS_ENDPOINT);
  const [job, setJob] = useState<any | null>(null);
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchJob = async () => {
      if (entityId) {
        const response = await get(entityId);
        if (response.data) {
          setJob(response.data);
        }
      }
    };

    const handleJobUpdated = () => {
      fetchJob();
    };

    const handleCounterAdded = () => {
      fetchJob();
    };

    window.addEventListener("jobUpdated", handleJobUpdated);
    window.addEventListener("equipmentCounterAdded", handleCounterAdded);
    fetchJob();
    return () => {
      window.removeEventListener("jobUpdated", handleJobUpdated);
      window.removeEventListener("equipmentCounterAdded", handleCounterAdded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewJobs) {
    return null;
  }

  if (!job) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {job.number || job.id}
          </Typography>
          {job.status_display && (
            <Chip
              label={job.status_display}
              color={JOB_STATUS_COLOR_MAP[job.status] ?? "default"}
              size="small"
            />
          )}
        </Box>
        {job.rejection_reason && (
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("Rejection reason")}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {job.rejection_reason}
            </Typography>
          </Alert>
        )}
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {job.number && (
            <DetailRow label={t("Number")}>
              <EntityReferenceLink
                entityType="job"
                entityId={job.id}
                label={job.number}
                canPreview={canViewJobs}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          <DetailRow label={t("Kind")}>
            <Chip
              label={job.kind_display || "-"}
              color={job.kind === "C" ? (JOB_KIND_COLOR_MAP[job.kind] ?? "default") : undefined}
              sx={job.kind === "C" ? undefined : getJobKindChipSx(job.kind)}
              size="small"
            />
          </DetailRow>
          {job.client && (
            <DetailRow label={t("Client")}>
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                <EntityReferenceLink
                  entityType="client"
                  entityId={job.client?.id || job.client_id}
                  label={job.client.short_name || job.client.name}
                  canPreview={canViewClients}
                  stopPropagation={false}
                />
                {job.client.address && <AddressAccordeon address={job.client.address} />}
              </Box>
            </DetailRow>
          )}
          {job.planned_for && (
            <DetailRow label={t("Planned For")}>
              <Typography variant="subtitle1">{formatDate(job.planned_for)}</Typography>
            </DetailRow>
          )}
          {job.description && (
            <DetailRow label={t("Description")}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                }}
              >
                {job.description}
              </Typography>
            </DetailRow>
          )}
          {job.complaint_for_number && (
            <DetailRow label={t("Complaint For")}>
              <EntityReferenceLink
                entityType="job"
                entityId={job.complaint_for_id}
                label={job.complaint_for_number}
                canPreview={canViewJobs}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          <DetailRow label={t("Created By")}>
            <Typography variant="subtitle1">
              {[job.created_by?.first_name, job.created_by?.last_name].filter(Boolean).join(" ") ||
                job.created_by?.email ||
                "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Finished At")}>
            <Typography variant="subtitle1">{formatDateTime(job.finished_at) || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Assigned To")} noBorder>
            {job.assigned_to_names && job.assigned_to_names.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {job.assigned_to_names.map((name: string, index: number) => (
                  <Chip key={index} label={name} size="small" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                -
              </Typography>
            )}
          </DetailRow>
        </Box>
      </Box>

      <Divider />

      {/* Tasks Section (each task: Issues block, Services block, then equipment, counter, description) */}
      {(job.tasks ?? job.services) && (job.tasks ?? job.services).length > 0 && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Tasks")} ({(job.tasks ?? job.services).length})
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {(job.tasks ?? job.services).map((task: JobTask) => (
                <Box
                  key={task.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Issues (separate block) */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t("Issues")}
                      </Typography>
                      {task.issues && task.issues.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                          {task.issues.map((issue: { code?: string | null; title?: string; color?: string | null }, index: number) => {
                            const title = issue.title || "—";
                            const label = issue.code ? `${title} (${issue.code})` : title;
                            return (
                              <Chip
                                key={index}
                                label={label}
                                size="small"
                                sx={
                                  issue.color
                                    ? {
                                        bgcolor: issue.color,
                                        color: "white",
                                        "& .MuiChip-label": { color: "white" },
                                      }
                                    : {
                                        bgcolor: "rgba(128, 128, 128, 0.25)",
                                        color: "text.secondary",
                                      }
                                }
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>

                    {/* Services (separate block) */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t("Services")}
                      </Typography>
                      {task.services && task.services.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                          {task.services.map((svc: { title?: string; color?: string | null }, index: number) => (
                            <Chip
                              key={index}
                              label={svc.title || "—"}
                              size="small"
                              sx={
                                svc.color
                                  ? {
                                      bgcolor: svc.color,
                                      color: "white",
                                      "& .MuiChip-label": { color: "white" },
                                    }
                                  : {
                                      bgcolor: "rgba(128, 128, 128, 0.25)",
                                      color: "text.secondary",
                                    }
                              }
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>

                    {/* Equipment, counter, description */}
                    {task.equipment && (
                      <>
                        <Typography variant="subtitle1">
                          <strong>{t("Equipment")}:</strong>{" "}
                          <EntityReferenceLink
                            entityType="equipment"
                            entityId={task.equipment.id}
                            label={task.equipment.full_name || "-"}
                            canPreview={canViewEquipment}
                            stopPropagation={false}
                          />
                        </Typography>
                        {task.equipment.installation_address && (
                          <Box sx={{ ml: 2 }}>
                            <AddressAccordeon
                              address={task.equipment.installation_address}
                              defaultExpanded
                              title={`${t("Installation Address")} & ${t("Opening Hours")}`}
                            />
                          </Box>
                        )}
                        {Array.isArray(task.equipment.attribute_values) &&
                          task.equipment.attribute_values.length > 0 && (
                            <Box sx={{ pl: 2, pt: 1 }}>
                              <EquipmentAttributeValuesAccordion
                                attributeValues={task.equipment.attribute_values}
                              />
                            </Box>
                          )}
                      </>
                    )}
                    <TaskCounterTable counters={task.counters} />
                    <TaskUsedPartsTable
                      usedParts={(job.used_parts ?? []).filter(
                        (usedPart: JobUsedPart) => String(usedPart.job_task_id) === String(task.id)
                      )}
                    />
                    {task.description && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          <strong>{t("Description")}:</strong>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            color: "text.secondary",
                            pl: 2,
                          }}
                        >
                          {task.description}
                        </Typography>
                      </Box>
                    )}
                    {task.finished_at && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t("Finished At")}:</strong> {formatDateTime(task.finished_at)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Attachments */}
      {job.attachments && job.attachments.length > 0 && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Attachments")} ({job.attachments.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {job.attachments.map((att: { id: number; attachment?: string }) => (
                <Chip
                  key={att.id}
                  component="a"
                  href={att.attachment || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  label={att.attachment ? att.attachment.replace(/^.*\//, "") : t("Attachment")}
                  size="small"
                  clickable
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(job.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(job.updated_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Status Changed At")}:</strong>{" "}
            {job.status_changed_at ? formatDateTime(job.status_changed_at) : "-"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
